import { Component, OnInit, inject } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { catchError, map, Observable } from 'rxjs';
import { User } from 'src/app/entities/user/user';
import { UserRepository } from 'src/app/services/remote-data-service/repositories/user-repository/user.repository';
import { StoreService } from 'src/app/services/store-service/store.service';
import { CommonModule } from '@angular/common';
import { FiltersService } from 'src/app/services/filters-service/filters.service';
import { OperationType } from 'src/app/models/operation-types';
import { Roles } from 'src/app/services/remote-data-service/repositories/user-repository/user.repository';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';
import { MembersMutationsComponent } from 'src/app/components/atoms/mutations/members-mutations/members-mutations.component';
import { EntityItemsAnnihilatorComponent } from 'src/app/components/atoms/mutations/entity-items-annihilator/entity-items-annihilator.component';

interface OrderedUsers {
  letter: string;
  users: User[]
}

@Component({
  selector: 'app-members-page',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatTableModule,
    MatSortModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule
  ],
  templateUrl: './members-page.component.html',
  styleUrl: './members-page.component.scss'
})
export class MembersPageComponent implements OnInit {
  users: User[] = [];
  users$: Observable<User[]>;
  groupedUsers: OrderedUsers[] = [];

  public form = new FormGroup({
    name: new FormControl('', Validators.required),
  });

  isManager = false;

  readonly dialog = inject(MatDialog);

  constructor(
    protected store: StoreService,
    protected userRep: UserRepository,
    protected filters: FiltersService,
    protected snackbar: SnackbarService
  ) {
    this.users$ = store.users;

    this.users$.pipe(
      catchError((err) => {
        console.error(err);
        return []
      }),
      map((users) => {
        this.users = users;
        return users;
      }),
      map((users) => {
        this.orderUsers(users)
      })
    ).subscribe();
  }

  ngOnInit(): void {
    this.filters.setUserFilters({
      limit: 99999
    })

    if (this.filters.hasFormValues('usersFilters')) {
      this.form.patchValue(
        this.filters.getFormValues<{ name: string }>('usersFilters') || {}
      );
    }

    this.form.valueChanges.subscribe((changes) => {
      this.filters.saveFormValues('usersFilters', changes);
      this.filters.setUserFilters({
        name: changes.name || undefined,
      });
    });

    this.userRep.getCurrentUser().subscribe((user) => {
      this.isManager = user.roles.includes(Roles.chronos_manager)
    })
  }

  orderUsers(usersRes: User[]) {
    const users = usersRes.sort((a, b) => a.name.localeCompare(b.name));

    const grouped = users.reduce((initial, user) => {
      const letter = user.name.charAt(0).toUpperCase();
      const group = initial.find(g => g.letter === letter);
      if (group) {
        group.users.push(user);
      } else {
        initial.push({ letter: letter, users: [user] });
      }
      return initial;
    }, [] as OrderedUsers[]);

    this.groupedUsers = grouped.sort((a, b) => a.letter.localeCompare(b.letter));
  }

  openCreateUserDialog(
    enterAnimationDuration: string,
    exitAnimationDuration: string
  ): void {
    this.dialog.open(MembersMutationsComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: '80dvw',
      maxHeight: '80dvh',
      enterAnimationDuration,
      exitAnimationDuration,
      data: {
        operation: OperationType.CREATE
      }
    });
  }

  openUpdateUserDialog(
    userId: string,
    enterAnimationDuration: string,
    exitAnimationDuration: string
  ): void {
    this.dialog.open(MembersMutationsComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: '80dvw',
      maxHeight: '80dvh',
      enterAnimationDuration,
      exitAnimationDuration,
      data: {
        operation: OperationType.UPDATE,
        id: userId
      }
    });
  }

  deleteUser(user: User) {
    this.userRep.deleteUser(user).pipe(
      catchError(() => {
        return []
      })
    ).subscribe(() => {
      const users = this.users.filter(e => e !== user);
      if (users) {
        this.users = users;
        this.snackbar.showSuccess('Deleted user');
        this.orderUsers(users);
      }
    })
  }


  openItemAnnihilatorDialog(
    enterAnimationDuration: string,
    exitAnimationDuration: string
  ): void {
    this.dialog.open(EntityItemsAnnihilatorComponent<User>, {
      width: 'auto',
      height: 'auto',
      maxWidth: '80dvw',
      maxHeight: '80dvh',
      enterAnimationDuration,
      exitAnimationDuration,
      data: {
        entities: this.users,
      }
    });
  }
}
